/**
 * Kingdom Factor Contact Page
 * - Hero section with contact info and schedule call CTA
 * - Embedded High Level form
 */

import { SeoHead } from "@/components/SeoHead"
import { Mail, ArrowRight } from "lucide-react"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { GetStartedDialog } from "@/components/GetStartedDialog"

export default function Contact({ seo, settings = {} }) {
  const { scheduleCallUrl = "#" } = settings
  const [getStartedOpen, setGetStartedOpen] = useState(false)

  // Load High Level form script
  useEffect(() => {
    const script = document.createElement("script")
    script.src = "https://api.gohighroad.com/js/form_embed.js"
    script.async = true
    document.body.appendChild(script)
    return () => {
      document.body.removeChild(script)
    }
  }, [])

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="contact" scheduleCallUrl={scheduleCallUrl} />

        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-zinc-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
              CONTACT <span className="text-kf-red">US</span>
            </h1>
            <p className="text-zinc-600 text-lg mb-8 max-w-2xl mx-auto">
              We'd love to hear from you. Whether you have questions about joining a peer group, becoming a coach, or anything else—reach out and let's connect.
            </p>

            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
              <a
                href="mailto:info@kingdomfactor.us"
                className="flex items-center gap-2 text-zinc-700 hover:text-kf-red transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>info@kingdomfactor.us</span>
              </a>
            </div>

            {/* Get Started CTA */}
            <div className="mb-4">
              <p className="text-zinc-500 text-sm mb-3">Ready to get started? Let us connect you with a coach.</p>
              <Button variant="gradient" size="lg" onClick={() => setGetStartedOpen(true)}>
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-2">
                SEND US A <span className="text-kf-red">MESSAGE</span>
              </h2>
              <p className="text-zinc-600">
                Fill out the form below and we'll get back to you shortly.
              </p>
            </div>

            {/* High Level Form Embed */}
            <div className="bg-zinc-50 p-6 rounded-lg">
              <iframe
                src="https://api.gohighroad.com/widget/form/2SkzEn1gT5z5ZQf5vxeJ"
                style={{
                  width: "100%",
                  height: "505px",
                  border: "none",
                  borderRadius: "3px",
                }}
                id="inline-2SkzEn1gT5z5ZQf5vxeJ"
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Contact Form"
                data-height="505"
                data-layout-iframe-id="inline-2SkzEn1gT5z5ZQf5vxeJ"
                data-form-id="2SkzEn1gT5z5ZQf5vxeJ"
                title="Contact Form"
              />
            </div>
          </div>
        </section>

        <Footer activePage="contact" />
      </div>

      <GetStartedDialog open={getStartedOpen} onOpenChange={setGetStartedOpen} />
    </>
  )
}
